import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Plus, Edit2, Trash2, GripVertical, Eye, EyeOff,
  Rocket, Package, Truck, Wallet, TrendingUp, Settings,
  Play, FileText, CheckSquare, Users, BarChart3, BookOpen,
  Youtube, Video, Link2, Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for sections
const iconOptions = [
  { value: 'Rocket', label: 'Rocket', icon: Rocket },
  { value: 'Package', label: 'Package', icon: Package },
  { value: 'Truck', label: 'Truck', icon: Truck },
  { value: 'Wallet', label: 'Wallet', icon: Wallet },
  { value: 'TrendingUp', label: 'Trending', icon: TrendingUp },
  { value: 'Settings', label: 'Settings', icon: Settings },
  { value: 'BookOpen', label: 'Book', icon: BookOpen },
];

// Color options for sections
const colorOptions = [
  { value: '#10B981', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6B7280', label: 'Gray' },
  { value: '#EF4444', label: 'Red' },
];

const AdminTrainingManagement = () => {
  const [sections, setSections] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [vendorProgress, setVendorProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sections');

  // Modal states
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [moduleModalOpen, setModuleModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form states
  const [sectionForm, setSectionForm] = useState({
    title: '',
    description: '',
    icon: 'BookOpen',
    color: '#3B82F6',
    order: 0,
    is_active: true,
    admin_only: false,
  });

  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    content_type: 'article',
    content: '',
    video_source_type: 'none',
    video_url: '',
    video_media_id: '',
    checklist_items: [],
    duration_minutes: null,
    order: 0,
    is_active: true,
    is_trackable: false,
    admin_only: false,
    section_id: '',
  });

  const [checklistInput, setChecklistInput] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);

  useEffect(() => {
    fetchData();
    fetchMediaFiles();
  }, []);

  const fetchMediaFiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/files`);
      // Filter for video files
      const videos = res.data.filter(f => 
        f.mime_type?.startsWith('video/') || 
        f.filename?.match(/\.(mp4|webm|mov|avi)$/i)
      );
      setMediaFiles(videos);
    } catch (error) {
      console.error('Error fetching media files:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sectionsRes, analyticsRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/training/sections`, { params: { include_inactive: true, admin_view: true } }),
        axios.get(`${API_URL}/api/training/admin/analytics`),
        axios.get(`${API_URL}/api/training/admin/vendor-progress`),
      ]);
      setSections(sectionsRes.data);
      setAnalytics(analyticsRes.data);
      setVendorProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  // Section CRUD
  const handleSaveSection = async () => {
    try {
      if (selectedSection) {
        await axios.put(`${API_URL}/api/training/sections/${selectedSection.id}`, sectionForm);
        toast.success('Section updated successfully');
      } else {
        await axios.post(`${API_URL}/api/training/sections`, sectionForm);
        toast.success('Section created successfully');
      }
      setSectionModalOpen(false);
      resetSectionForm();
      fetchData();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Failed to save section');
    }
  };

  const handleEditSection = (section) => {
    setSelectedSection(section);
    setSectionForm({
      title: section.title,
      description: section.description || '',
      icon: section.icon || 'BookOpen',
      color: section.color || '#3B82F6',
      order: section.order,
      is_active: section.is_active,
      admin_only: section.admin_only,
    });
    setSectionModalOpen(true);
  };

  const resetSectionForm = () => {
    setSelectedSection(null);
    setSectionForm({
      title: '',
      description: '',
      icon: 'BookOpen',
      color: '#3B82F6',
      order: sections.length,
      is_active: true,
      admin_only: false,
    });
  };

  // Module CRUD
  const handleSaveModule = async () => {
    try {
      const moduleData = {
        ...moduleForm,
        duration_minutes: moduleForm.duration_minutes ? parseInt(moduleForm.duration_minutes) : null,
      };

      if (selectedModule) {
        await axios.put(`${API_URL}/api/training/modules/${selectedModule.id}`, moduleData);
        toast.success('Module updated successfully');
      } else {
        await axios.post(`${API_URL}/api/training/modules`, moduleData);
        toast.success('Module created successfully');
      }
      setModuleModalOpen(false);
      resetModuleForm();
      fetchData();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Failed to save module');
    }
  };

  const handleEditModule = (module, sectionId) => {
    setSelectedModule(module);
    setModuleForm({
      title: module.title,
      description: module.description || '',
      content_type: module.content_type,
      content: module.content || '',
      video_source_type: module.video_source_type || 'none',
      video_url: module.video_url || '',
      video_media_id: module.video_media_id || '',
      checklist_items: module.checklist_items || [],
      duration_minutes: module.duration_minutes || null,
      order: module.order,
      is_active: module.is_active,
      is_trackable: module.is_trackable,
      admin_only: module.admin_only,
      section_id: sectionId,
    });
    setModuleModalOpen(true);
  };

  const handleAddModule = (sectionId, moduleCount) => {
    resetModuleForm();
    setModuleForm(prev => ({
      ...prev,
      section_id: sectionId,
      order: moduleCount,
    }));
    setModuleModalOpen(true);
  };

  const resetModuleForm = () => {
    setSelectedModule(null);
    setModuleForm({
      title: '',
      description: '',
      content_type: 'article',
      content: '',
      video_source_type: 'none',
      video_url: '',
      video_media_id: '',
      checklist_items: [],
      duration_minutes: null,
      order: 0,
      is_active: true,
      is_trackable: false,
      admin_only: false,
      section_id: '',
    });
    setChecklistInput('');
  };

  // Delete handlers
  const handleDelete = async () => {
    try {
      if (deleteTarget.type === 'section') {
        await axios.delete(`${API_URL}/api/training/sections/${deleteTarget.id}`);
        toast.success('Section deleted successfully');
      } else {
        await axios.delete(`${API_URL}/api/training/modules/${deleteTarget.id}`);
        toast.success('Module deleted successfully');
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const confirmDelete = (type, id, title) => {
    setDeleteTarget({ type, id, title });
    setDeleteDialogOpen(true);
  };

  // Checklist management
  const addChecklistItem = () => {
    if (checklistInput.trim()) {
      setModuleForm(prev => ({
        ...prev,
        checklist_items: [...prev.checklist_items, checklistInput.trim()],
      }));
      setChecklistInput('');
    }
  };

  const removeChecklistItem = (index) => {
    setModuleForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.filter((_, i) => i !== index),
    }));
  };

  const getIconComponent = (iconName) => {
    const found = iconOptions.find(opt => opt.value === iconName);
    return found ? found.icon : BookOpen;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Academy Management</h1>
            <p className="text-gray-500">Create and manage training content for vendors</p>
          </div>
          <Button onClick={() => { resetSectionForm(); setSectionModalOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Section
          </Button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Sections</p>
                    <p className="text-2xl font-bold">{analytics.total_sections}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Modules</p>
                    <p className="text-2xl font-bold">{analytics.total_modules}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vendors Engaged</p>
                    <p className="text-2xl font-bold">{analytics.vendors_engaged}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg. Completion</p>
                    <p className="text-2xl font-bold">{analytics.avg_modules_completed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="sections">Sections & Modules</TabsTrigger>
            <TabsTrigger value="progress">Vendor Progress</TabsTrigger>
          </TabsList>

          {/* Sections Tab */}
          <TabsContent value="sections" className="mt-4">
            <div className="space-y-4">
              {sections.map((section) => {
                const IconComponent = getIconComponent(section.icon);
                return (
                  <Card key={section.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: `${section.color}20` }}
                          >
                            <IconComponent className="h-5 w-5" style={{ color: section.color }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg">{section.title}</CardTitle>
                              {section.admin_only && (
                                <Badge variant="outline">Admin Only</Badge>
                              )}
                              {!section.is_active && (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </div>
                            <CardDescription>{section.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddModule(section.id, section.modules?.length || 0)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Module
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditSection(section)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => confirmDelete('section', section.id, section.title)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {section.modules?.length > 0 && (
                      <CardContent>
                        <div className="border rounded-lg divide-y">
                          {section.modules.map((module, index) => (
                            <div
                              key={module.id}
                              className="flex items-center justify-between p-3 hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-3">
                                <GripVertical className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{module.title}</span>
                                    {module.content_type === 'video' && (
                                      <Play className="h-3 w-3 text-blue-500" />
                                    )}
                                    {module.content_type === 'checklist' && (
                                      <CheckSquare className="h-3 w-3 text-green-500" />
                                    )}
                                    {module.admin_only && (
                                      <Badge variant="outline" className="text-xs">Admin</Badge>
                                    )}
                                    {module.is_trackable && (
                                      <Badge variant="secondary" className="text-xs">Tracked</Badge>
                                    )}
                                    {!module.is_active && (
                                      <EyeOff className="h-3 w-3 text-gray-400" />
                                    )}
                                  </div>
                                  {module.description && (
                                    <p className="text-sm text-gray-500 truncate max-w-md">
                                      {module.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditModule(module, section.id)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => confirmDelete('module', module.id, module.title)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Vendor Progress Tab */}
          <TabsContent value="progress" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Vendor Training Progress</CardTitle>
                <CardDescription>Track how vendors are progressing through the training academy</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>In Progress</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Last Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorProgress.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                          No vendor progress data yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      vendorProgress.map((vp) => (
                        <TableRow key={vp.vendor_id}>
                          <TableCell className="font-medium">{vp.vendor_name}</TableCell>
                          <TableCell>{vp.vendor_email}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{vp.completed_modules} / {vp.total_trackable}</Badge>
                          </TableCell>
                          <TableCell>{vp.in_progress_modules}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${vp.percentage}%` }}
                                />
                              </div>
                              <span className="text-sm">{vp.percentage}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {new Date(vp.last_activity).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Section Modal */}
      <Dialog open={sectionModalOpen} onOpenChange={setSectionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedSection ? 'Edit Section' : 'Add Section'}</DialogTitle>
            <DialogDescription>
              {selectedSection ? 'Update the training section details' : 'Create a new training section'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={sectionForm.title}
                onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                placeholder="e.g., Get Started"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={sectionForm.description}
                onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                placeholder="Describe what this section covers..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={sectionForm.icon}
                  onValueChange={(value) => setSectionForm({ ...sectionForm, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={sectionForm.color}
                  onValueChange={(value) => setSectionForm({ ...sectionForm, color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: opt.value }} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={sectionForm.order}
                onChange={(e) => setSectionForm({ ...sectionForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={sectionForm.is_active}
                  onCheckedChange={(checked) => setSectionForm({ ...sectionForm, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={sectionForm.admin_only}
                  onCheckedChange={(checked) => setSectionForm({ ...sectionForm, admin_only: checked })}
                />
                <Label>Admin Only</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSection} disabled={!sectionForm.title}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Modal */}
      <Dialog open={moduleModalOpen} onOpenChange={setModuleModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedModule ? 'Edit Module' : 'Add Module'}</DialogTitle>
            <DialogDescription>
              {selectedModule ? 'Update the training module details' : 'Create a new training module'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="moduleTitle">Title</Label>
              <Input
                id="moduleTitle"
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                placeholder="e.g., How to create your first listing"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="moduleDescription">Description</Label>
              <Textarea
                id="moduleDescription"
                value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                placeholder="Brief description of what this module covers..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select
                  value={moduleForm.content_type}
                  onValueChange={(value) => setModuleForm({ ...moduleForm, content_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="article">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Article
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Play className="h-4 w-4" />
                        Video
                      </div>
                    </SelectItem>
                    <SelectItem value="checklist">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="h-4 w-4" />
                        Checklist
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={moduleForm.duration_minutes || ''}
                  onChange={(e) => setModuleForm({ ...moduleForm, duration_minutes: e.target.value })}
                  placeholder="e.g., 10"
                />
              </div>
            </div>

            {/* Video Source Section - Always visible */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <Video className="h-5 w-5 text-gray-600" />
                <Label className="text-base font-medium">Video Content</Label>
              </div>
              
              <div className="space-y-2">
                <Label>Video Source</Label>
                <Select
                  value={moduleForm.video_source_type}
                  onValueChange={(value) => setModuleForm({ 
                    ...moduleForm, 
                    video_source_type: value,
                    video_url: value !== 'youtube' ? '' : moduleForm.video_url,
                    video_media_id: value !== 'media' ? '' : moduleForm.video_media_id,
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select video source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <EyeOff className="h-4 w-4" />
                        No Video (Placeholder)
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube">
                      <div className="flex items-center gap-2">
                        <Youtube className="h-4 w-4 text-red-500" />
                        YouTube / Vimeo Link
                      </div>
                    </SelectItem>
                    <SelectItem value="media">
                      <div className="flex items-center gap-2">
                        <Image className="h-4 w-4 text-blue-500" />
                        Media Library
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* YouTube URL Input */}
              {moduleForm.video_source_type === 'youtube' && (
                <div className="space-y-2">
                  <Label htmlFor="videoUrl">
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      Embed URL
                    </div>
                  </Label>
                  <Input
                    id="videoUrl"
                    value={moduleForm.video_url}
                    onChange={(e) => setModuleForm({ ...moduleForm, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/embed/VIDEO_ID"
                  />
                  <p className="text-xs text-gray-500">
                    Use the embed URL format. For YouTube: youtube.com/embed/VIDEO_ID
                  </p>
                </div>
              )}

              {/* Media Library Select */}
              {moduleForm.video_source_type === 'media' && (
                <div className="space-y-2">
                  <Label>Select from Media Library</Label>
                  <Select
                    value={moduleForm.video_media_id}
                    onValueChange={(value) => setModuleForm({ ...moduleForm, video_media_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a video file" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaFiles.length === 0 ? (
                        <SelectItem value="_none" disabled>No video files in Media Library</SelectItem>
                      ) : (
                        mediaFiles.map((file) => (
                          <SelectItem key={file.id} value={file.id}>
                            <div className="flex items-center gap-2">
                              <Play className="h-4 w-4" />
                              {file.filename}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Upload videos via Content → Media Gallery first
                  </p>

                  {/* Preview selected video */}
                  {moduleForm.video_media_id && (
                    <div className="p-2 bg-white rounded border flex items-center gap-2">
                      <Play className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">
                        {mediaFiles.find(f => f.id === moduleForm.video_media_id)?.filename || 'Video selected'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* No video placeholder message */}
              {moduleForm.video_source_type === 'none' && (
                <p className="text-sm text-gray-500 italic">
                  A "Video Coming Soon" placeholder will be shown to users.
                </p>
              )}
            </div>

            {/* Article Content */}
            <div className="space-y-2">
              <Label htmlFor="content">Text Content (HTML supported)</Label>
              <Textarea
                id="content"
                value={moduleForm.content}
                onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                placeholder="<p>Your content here...</p>"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                This content appears below the video. Use HTML for formatting.
              </p>
            </div>

            {/* Checklist Items */}
            {moduleForm.content_type === 'checklist' && (
              <div className="space-y-2">
                <Label>Checklist Items</Label>
                <div className="flex gap-2">
                  <Input
                    value={checklistInput}
                    onChange={(e) => setChecklistInput(e.target.value)}
                    placeholder="Add a checklist item..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                  />
                  <Button type="button" onClick={addChecklistItem}>Add</Button>
                </div>
                {moduleForm.checklist_items.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {moduleForm.checklist_items.map((item, index) => (
                      <div key={item.id || item.name || `${item.title || "item"}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{index + 1}. {item}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeChecklistItem(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="moduleOrder">Display Order</Label>
              <Input
                id="moduleOrder"
                type="number"
                value={moduleForm.order}
                onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={moduleForm.is_active}
                  onCheckedChange={(checked) => setModuleForm({ ...moduleForm, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={moduleForm.is_trackable}
                  onCheckedChange={(checked) => setModuleForm({ ...moduleForm, is_trackable: checked })}
                />
                <Label>Track Progress</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={moduleForm.admin_only}
                  onCheckedChange={(checked) => setModuleForm({ ...moduleForm, admin_only: checked })}
                />
                <Label>Admin Only</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveModule} disabled={!moduleForm.title || !moduleForm.section_id}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"?
              {deleteTarget?.type === 'section' && (
                <span className="block mt-2 text-red-500">
                  This will also delete all modules within this section.
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AdminTrainingManagement;
